import { OfChatMessagesService, VaultMediaType } from '@api'
import { attach, createEffect, createStore } from 'effector'

import { setFanId } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { $vaultMediasMap } from '@/models/vault/medias'
import type { SentMediaMessage } from '@/modules/chatting/types.ts'
import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { handleError } from '@/utils/error'
import { getVaultImageUrl } from '@/utils/url.ts'

const audioImage =
  'https://cdn.pixabay.com/photo/2017/02/27/19/34/microphone-2104091_1280.png'

export const getTimelineDataFx = attach({
  name: 'get timeline data',
  source: {
    vaultMediasMap: $vaultMediasMap,
    creatorId: $creatorId,
  },
  mapParams: (
    ppvTimelineData: SentMediaMessage[],
    { vaultMediasMap, creatorId },
  ) => ({
    ppvTimelineData,
    vaultMediasMap,
    creatorId: creatorId!,
  }),
  effect: createEffect(
    async ({
      ppvTimelineData,
      vaultMediasMap,
      creatorId,
    }: {
      ppvTimelineData: SentMediaMessage[]
      vaultMediasMap: Record<number, StreamableVaultMedia>
      creatorId: number
    }) => {
      return Promise.all(
        ppvTimelineData
          .filter((item) => item.price > 0 && item.media.length > 0)
          .flatMap((item) =>
            item.media.reduce(
              (result, media) => {
                const originalMedia = vaultMediasMap?.[media.id]

                if (
                  originalMedia?.type === VaultMediaType.AUDIO &&
                  !result.preview
                ) {
                  // if no preview at all and only audio - set preview for audio
                  result.preview = audioImage
                } else if (
                  (!result.preview || result.preview === audioImage) &&
                  [VaultMediaType.PHOTO, VaultMediaType.VIDEO].includes(
                    // @ts-ignore
                    originalMedia?.type,
                  )
                ) {
                  result.preview = originalMedia?.files.thumb?.url || ''
                }

                if (originalMedia?.type === VaultMediaType.PHOTO) {
                  result.photoCount++
                } else if (originalMedia?.type === VaultMediaType.VIDEO) {
                  result.videoCount++
                } else if (originalMedia?.type === VaultMediaType.AUDIO) {
                  result.audioCount++
                }

                return result
              },
              {
                createdAt: item.createdAt,
                price: item.price,
                isOpened: item.isOpened,
                photoCount: 0,
                audioCount: 0,
                videoCount: 0,
                text: item.text,
                preview: '',
                messageId: item.id,
              },
            ),
          )
          .map(async (item) => {
            if (item.preview === audioImage) {
              return {
                ...item,
                preview: item.preview,
              }
            }

            const vaultImageUrl = await getVaultImageUrl(
              item.preview,
              creatorId!,
            )

            if (vaultImageUrl) {
              return {
                ...item,
                preview: vaultImageUrl,
              }
            }

            try {
              const messageData = ppvTimelineData.find(
                (msg) => msg.id === item.messageId,
              )

              if (!messageData?.fanId) {
                return {
                  ...item,
                  preview: '',
                }
              }

              const detailedMessage =
                await OfChatMessagesService.getDetailedChatMessage({
                  creatorId: creatorId!,
                  userId: messageData.fanId,
                  messageId: item.messageId,
                })

              const firstMediaPreview =
                detailedMessage.message?.media?.[0]?.files?.thumb?.url ||
                detailedMessage.message?.media?.[0]?.files?.preview?.url ||
                ''

              return {
                ...item,
                preview: firstMediaPreview,
              }
            } catch (error) {
              handleError(
                error,
                'Failed to fetch detailed message for preview:',
              )

              return {
                ...item,
                preview: '',
              }
            }
          }),
      )
    },
  ),
})

export const $timelineData = createStore<TimelineData[]>([])
  .on(getTimelineDataFx.doneData, (_, data) => data)
  .reset(setFanId)
